/**
 * Lean Setup Wizard validation schemas.
 *
 * Mirrors the constraints already enforced by:
 *   - DB CHECK constraints on the `technicians` table
 *   - the existing `<TechnicianForm>` component
 *
 * Source-of-truth alignment:
 *   - skill_tier: 1..10 (per migration 20251026054907 which widened the
 *     original 1..5 CHECK; the existing TechnicianForm slider is 1..10).
 *   - efficiency_percentage: 10..500
 *   - buffer_percentage: 0..100, or unset (B-W4: unset = "No buffer set", planned as 0 %)
 *   - NO legacy `role` (Apprentice / Technician / Foreman "Tech
 *     Classification"). `technicians.role` is a compatibility column the
 *     server fills; the only role system a manager chooses is the work
 *     roles. See LEGACY_TECHNICIAN_ROLE_DOCTRINE in
 *     src/utils/technicianConfigurationPayload.ts.
 *   - bays_allocated: ≥ 0 (FOH / managers / advisors can carry 0 bays)
 *
 * Specialty bay rules:
 *   - A specialty selection CONVERTS one of the primary tech's existing
 *     bays into that specialty. It does NOT add a new bay.
 *   - The primary tech for a specialty must have ≥ 1 bay allocated.
 *   - A tech with 0 bays can never be the primary for a specialty.
 *   - A tech who is primary for N specialties needs at least N bays.
 */

import { z } from 'zod';
import {
  BAY_TYPE_ENUM_VALUES,
  DEFAULT_SETUP_BAY_TYPE,
  bayTypeToLabel,
  type BayTypeEnum,
} from '@/utils/bayTypeLabels';

// ---------------------------------------------------------------
// Page 1 — Shop basics
// ---------------------------------------------------------------

const TIME_HHMM = /^\d{2}:\d{2}$/;

export const dayHoursSchema = z.object({
  open: z.string().regex(TIME_HHMM, 'open must be HH:MM'),
  close: z.string().regex(TIME_HHMM, 'close must be HH:MM'),
});

export const weeklyHoursSchema = z.record(z.string(), dayHoursSchema.optional());

export const page1Schema = z.object({
  name: z.string().trim().min(1, 'Shop name is required'),
  time_zone: z.string().trim().min(1, 'Time zone is required'),
  // Batch 8B: hours are NO LONGER a blocking required field.
  // - If the operator typed any day → those values flow through.
  // - If the operator skipped hours and the public-listing lookup
  //   found nothing either → `commitWizard` applies
  //   DEFAULT_SHOP_HOURS_MON_FRI_8_5 and tags
  //   `shop_profile.public_hours_source = 'default'`.
  // We keep the schema (so day-level open/close still validate as
  // HH:MM when set), but drop the "at least one day" refine so an
  // empty map is allowed.
  hours: weeklyHoursSchema,
  // Batch 8: optional address fields. Drives the public-business
  // lookup (`lookup-shop-public-info`) so we can prefill shop hours
  // from a public listing instead of CSV timestamps, and stores
  // lat/lng/place_id as the foundation for future map-based parking/
  // facility-layout inference. All optional — the wizard never
  // blocks Next on missing address.
  street_address: z.string().trim().max(200).optional().or(z.literal('')),
  city: z.string().trim().max(100).optional().or(z.literal('')),
  state: z.string().trim().max(100).optional().or(z.literal('')),
  postal_code: z.string().trim().max(20).optional().or(z.literal('')),
  country: z.string().trim().max(2).default('US'),
});

export type Page1Data = z.infer<typeof page1Schema>;

// ---------------------------------------------------------------
// Page 2 — People counts
// ---------------------------------------------------------------

export const page2Schema = z.object({
  num_technicians: z
    .number({ invalid_type_error: 'Number of technicians is required' })
    .int()
    .min(1, 'At least one technician is required to set up the shop'),
  num_advisors: z.number().int().min(0),
  num_managers: z.number().int().min(0),
});

export type Page2Data = z.infer<typeof page2Schema>;

// ---------------------------------------------------------------
// Page 3 — Team, bays, specialty, parking
// ---------------------------------------------------------------

// ---------------------------------------------------------------
// ONE role space on Step 3.
// ---------------------------------------------------------------
//
//   TEAM_ROLE_OPTIONS  → OptiBay/membership role for the wizard row.
//                        Canonical uppercase strings; map 1:1 to
//                        `memberships.role` for downstream batches
//                        that send invitations.
//
// There used to be a second: a "Tech Classification" (Apprentice /
// Technician / Foreman) written to `technicians.role`. It is gone. That
// column is a legacy compatibility field the server fills on create;
// a manager does not choose it. See LEGACY_TECHNICIAN_ROLE_DOCTRINE.
// ---------------------------------------------------------------

export const TEAM_ROLE_OPTIONS = [
  'TECHNICIAN',
  // WS-ELIG B-E7 (CORE-R:82 / CORE-W:73): detail work. Invited as STAFF + work
  // role DETAIL_SPECIALIST; their roster entry is created when they accept.
  'DETAIL_SPECIALIST',
  'SERVICE_WRITER',
  'MANAGER',
  'OWNER',
  'ORG_ADMIN',
] as const;
export type TeamRole = (typeof TEAM_ROLE_OPTIONS)[number];

/**
 * Human labels for TEAM_ROLE_OPTIONS — what the wizard shows in the
 * "Role" dropdown. The underlying value stays canonical uppercase so
 * downstream code (Batch 5 invite-sender) can pass straight through.
 */
export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  TECHNICIAN: 'Technician',
  DETAIL_SPECIALIST: 'Detail Specialist',
  SERVICE_WRITER: 'Advisor / Service Writer',
  MANAGER: 'Manager',
  OWNER: 'Owner',
  ORG_ADMIN: 'Org Admin',
};

/**
 * Specialty bay kinds. Values map directly to `public.bay_type` enum values:
 *   adas, alignment, alignment-adas, wash
 * (The enum also has 2-post, 4-post, scissor, etc. — those aren't
 * specialty selections in this wizard.)
 */
export const SPECIALTY_KINDS = ['adas', 'alignment', 'alignment-adas', 'wash'] as const;
export type SpecialtyKind = (typeof SPECIALTY_KINDS)[number];

/**
 * Reconcile a tech row's `bay_types` array to match a new
 * `bays_allocated` count (Batch 16-B).
 *
 *   - If the array is shorter than the new count, append
 *     `DEFAULT_SETUP_BAY_TYPE` (`'flat'`) for each missing slot so the
 *     operator's pre-existing choices for slots 0..N-1 are preserved
 *     and the new slots start from a sensible default.
 *   - If the array is longer, trim from the end so leading slots' types
 *     survive a "decrease the bay count" edit.
 *   - If `bays_allocated` is 0 (or undefined), return an empty array.
 *
 * Pure function — caller controls when to invoke it.
 */
export function reconcileBayTypesForCount(
  existing: ReadonlyArray<BayTypeEnum> | undefined,
  bays_allocated: number | undefined,
): BayTypeEnum[] {
  const target = Math.max(0, typeof bays_allocated === 'number' ? bays_allocated : 0);
  if (target === 0) return [];
  const out = Array.from(existing ?? []);
  if (out.length === target) return out;
  if (out.length < target) {
    while (out.length < target) out.push(DEFAULT_SETUP_BAY_TYPE);
    return out;
  }
  return out.slice(0, target);
}

// Batch 16-A: source the wizard's specialty labels from the canonical
// `bayTypeToLabel` so the setup surface, Birdseye, ShopFloorEditor, and
// InspectorPanel all render identical text. Pre-bump this map had two
// wizard-only labels — "ADAS + Alignment" (reversed) and "Wash / Detail"
// (extra "/ Detail" with no storage counterpart) — that didn't appear in
// any other surface and would have surfaced as confusing renames after
// the operator left onboarding.
export const SPECIALTY_LABELS: Record<SpecialtyKind, string> = {
  adas: bayTypeToLabel('adas'),
  alignment: bayTypeToLabel('alignment'),
  'alignment-adas': bayTypeToLabel('alignment-adas'),
  wash: bayTypeToLabel('wash'),
};

/**
 * Single row schema for Step 3's team table. Renamed from the legacy
 * `wizardTechSchema` (kept as an alias below for back-compat with
 * tests and inferenceLoader). Rows can now represent any team role,
 * not just technicians.
 *
 * Field model:
 *   - `team_role` is the discriminator. Defaults to TECHNICIAN so
 *     legacy callers / fixtures that omit it (existing tests,
 *     inferenceLoader output) continue to validate as technician
 *     rows without modification.
 *   - The per-tech operational fields are ALL optional at the schema
 *     level. The refinement below enforces them as required when
 *     team_role === TECHNICIAN. There is no `role` field: a legacy
 *     `role` key on a saved draft is stripped by zod and never sent.
 *   - Non-technician rows only need `name` + `team_role`.
 *
 * Storage contract: commitWizard FILTERS to TECHNICIAN rows before
 * calling create-technician-with-profile. Non-technician rows are
 * persisted as onboarding_state metadata only; invitations land in
 * Batch 5.
 */
// Roles for which the wizard surfaces the "Invite" checkbox. ORG_ADMIN
// is excluded here even though `app_role` accepts it — TeamManagement's
// established invite UI only exposes OWNER/MANAGER/SERVICE_WRITER/
// TECHNICIAN, and Batch 5 intentionally matches that surface. The Step
// 3 UI uses this list to decide whether the Invite checkbox is enabled.
export const INVITABLE_TEAM_ROLES: readonly TeamRole[] = [
  'TECHNICIAN',
  'DETAIL_SPECIALIST', // WS-ELIG B-E7: a Detail Specialist joins by invitation (their entry is created on acceptance)
  'SERVICE_WRITER',
  'MANAGER',
  'OWNER',
];

export function isInvitableTeamRole(role: TeamRole | undefined): boolean {
  return INVITABLE_TEAM_ROLES.includes((role ?? 'TECHNICIAN') as TeamRole);
}

export const wizardTeamMemberSchema = z
  .object({
    team_role: z.enum(TEAM_ROLE_OPTIONS).default('TECHNICIAN'),
    name: z.string().trim().min(1, 'Name is required'),
    // 1..10 per migration 20251026054907 (CHECK widened from 1..5).
    skill_tier: z.number().int().min(1).max(10).optional(),
    // Roadmap #7: prior trade background, as stated by the owner. Optional —
    // skipped means "not provided", never "new to the trade". Display context
    // only; it never feeds proficiency or assignment.
    experience_level: z.enum(['NEW_TO_TRADE', 'DEVELOPING', 'EXPERIENCED', 'VETERAN']).nullable().optional(),
    years_professional: z.number().int().min(0).max(70).nullable().optional(),
    // INTEL-EFF-RANGE-001: authoritative range 10–500% (matches the DB CHECK).
    efficiency_percentage: z.number().int().min(10).max(500).optional(),
    // CORE-G provenance. Present only when a person actually edited the
    // number; absent means the roster default nobody established.
    efficiency_source: z.enum(['manual']).optional(),
    // B-W4: absent / null = "No buffer set" (planned as 0 %); a recorded 0 stays 0.
    buffer_percentage: z.number().int().min(0).max(100).nullable().optional(),
    shift_start: z.string().regex(TIME_HHMM, 'shift_start must be HH:MM').optional(),
    shift_end: z.string().regex(TIME_HHMM, 'shift_end must be HH:MM').optional(),
    // 0 allowed — FOH / managers / advisors may carry no bay.
    bays_allocated: z.number().int().min(0).optional(),
    // Batch 16-B: per-bay-slot type picker. Length must equal
    // `bays_allocated` when both are set. Values come from the same
    // canonical Postgres enum the rest of the app reads/writes —
    // `BAY_TYPE_ENUM_VALUES` is the single source of truth (`shop_bays
    // .bay_type`). Optional so legacy callers (older tests, the import
    // path's first projection in `inferenceLoader.ts`) don't have to
    // supply it; the generator falls back to `DEFAULT_SETUP_BAY_TYPE`
    // per missing slot. Non-TECHNICIAN rows never need bay_types because
    // they don't produce shop_bays records (see `commitWizard.ts:240-242`).
    bay_types: z.array(z.enum(BAY_TYPE_ENUM_VALUES)).optional(),
    // Batch 5: invite-during-onboarding. `email` is always optional at
    // schema level; the refinement below requires a valid address when
    // `invite === true`. Empty-string is accepted so the UI can render
    // a controlled <input> without forcing the operator to clear it
    // when toggling Invite off.
    email: z.string().trim().email('Email is not valid').or(z.literal('')).optional(),
    invite: z.boolean().optional().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.team_role === 'TECHNICIAN') {
      // TECHNICIAN rows must carry the full operational field set —
      // the existing `technicians`-table write path in commitWizard
      // (via the `create-technician-with-profile` edge function) reads
      // all of them and would fail at the DB CHECK layer otherwise.
      const required: Array<'skill_tier' | 'efficiency_percentage' | 'shift_start' | 'shift_end' | 'bays_allocated'> = [
        'skill_tier',
        'efficiency_percentage',
        'shift_start',
        'shift_end',
        'bays_allocated',
      ];
      for (const field of required) {
        if (data[field] === undefined || data[field] === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `Technician rows require ${field}`,
          });
        }
      }
    }
    // Batch 16-B: when `bay_types` is supplied on a TECHNICIAN row, its
    // length must match `bays_allocated` exactly. We allow it to be
    // omitted entirely (the generator falls back to the canonical
    // 'flat' default for every missing slot) so older code paths that
    // only set `bays_allocated` keep working without a migration.
    if (
      data.team_role === 'TECHNICIAN' &&
      Array.isArray(data.bay_types) &&
      typeof data.bays_allocated === 'number'
    ) {
      if (data.bay_types.length !== data.bays_allocated) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bay_types'],
          message: `bay_types length (${data.bay_types.length}) must equal bays_allocated (${data.bays_allocated})`,
        });
      }
    }
    // Invite-on requires a valid email. The `email` field schema
    // already rejects malformed addresses, so the only remaining gap
    // is the empty-string sentinel that we deliberately accept.
    if (data.invite === true) {
      const email = data.email?.trim();
      if (!email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['email'],
          message: 'Email is required when Invite is checked',
        });
      }
    }
  });

/**
 * Back-compat alias. Tests and inferenceLoader continue to import
 * `wizardTechSchema` and `WizardTech`; they refer to the same schema
 * as the new `wizardTeamMemberSchema` / `WizardTeamMember`.
 */
export const wizardTechSchema = wizardTeamMemberSchema;
export type WizardTeamMember = z.infer<typeof wizardTeamMemberSchema>;
export type WizardTech = WizardTeamMember;

export const wizardSpecialtySchema = z.object({
  kind: z.enum(SPECIALTY_KINDS),
  primary_tech_index: z.number().int().min(0),
});

export type WizardSpecialty = z.infer<typeof wizardSpecialtySchema>;

export const page3Schema = z
  .object({
    technicians: z.array(wizardTechSchema).min(1),
    specialties: z.array(wizardSpecialtySchema),
    parking_spot_count: z.number().int().min(0),
  })
  .superRefine((data, ctx) => {
    // Specialty validation only considers TECHNICIAN rows — they're
    // the only ones with bays_allocated set. Specialties referencing
    // a non-technician row are a bug in the picker, but treat them
    // the same as missing-tech-index for the operator-facing message.
    const isTechnicianRow = (idx: number) =>
      data.technicians[idx]?.team_role === 'TECHNICIAN' ||
      // Default-TECHNICIAN: rows constructed via legacy fixtures that
      // omit team_role get the default after parse. Treat undefined
      // (pre-parse path) the same way for inputs that came in raw.
      (data.technicians[idx] && data.technicians[idx]!.team_role === undefined);

    // Every specialty primary tech must have at least 1 bay.
    for (const [si, spec] of data.specialties.entries()) {
      const tech = data.technicians[spec.primary_tech_index];
      if (!tech || !isTechnicianRow(spec.primary_tech_index)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['specialties', si, 'primary_tech_index'],
          message: `Specialty "${SPECIALTY_LABELS[spec.kind]}" references a missing technician`,
        });
        continue;
      }
      const allocated = tech.bays_allocated ?? 0;
      if (allocated < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['specialties', si, 'primary_tech_index'],
          message: `${tech.name || 'This person'} has 0 bays — they cannot be the primary for ${SPECIALTY_LABELS[spec.kind]}`,
        });
      }
    }
    // A tech who is primary for K specialties needs ≥ K bays so each
    // specialty can convert a distinct bay. (Combined specialties are
    // already a single 'alignment-adas' selection — no need to combine
    // further here.)
    const techSpecCount = new Map<number, number>();
    for (const spec of data.specialties) {
      techSpecCount.set(spec.primary_tech_index, (techSpecCount.get(spec.primary_tech_index) ?? 0) + 1);
    }
    for (const [ti, count] of techSpecCount.entries()) {
      const tech = data.technicians[ti];
      const allocated = tech?.bays_allocated ?? 0;
      if (tech && isTechnicianRow(ti) && count > allocated) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['specialties'],
          message: `${tech.name || 'A technician'} has ${allocated} bay(s) but is the primary for ${count} specialties — increase their bay count or pick a different primary`,
        });
      }
    }
  });

export type Page3Data = z.infer<typeof page3Schema>;

// ---------------------------------------------------------------
// Full wizard
// ---------------------------------------------------------------

export const wizardSchema = z.object({
  page1: page1Schema,
  page2: page2Schema,
  page3: page3Schema,
});

export type WizardData = z.infer<typeof wizardSchema>;

// ---------------------------------------------------------------
// Soft warnings (advisory, not blocking)
// ---------------------------------------------------------------

/**
 * A technician row with 0 bays_allocated is flagged for attention but
 * does NOT block submit.
 *
 * This used to exempt rows classified "Apprentice". That classification
 * no longer exists (see LEGACY_TECHNICIAN_ROLE_DOCTRINE), so every
 * TECHNICIAN row with no bay gets the advisory — which is true of all of
 * them: none appears on the Dayboard until a bay is assigned.
 *
 * Non-technician team_role rows (Advisor / Manager / Owner / Org Admin)
 * don't carry bays — they're filtered out before the warning check.
 */
export function softWarningsForPage3(data: Page3Data): string[] {
  const warnings: string[] = [];
  for (const tech of data.technicians) {
    if (tech.team_role && tech.team_role !== 'TECHNICIAN') continue;
    if (tech.bays_allocated === 0) {
      warnings.push(
        `${tech.name || 'A producing technician'} has 0 bays allocated — they will not appear on the Dayboard until a bay is assigned.`,
      );
    }
  }
  return warnings;
}
